'use client';

import { Button } from '@/components/ui/button';
import { CheckIcon, TagIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useCart } from './cart-context';

interface PromoCodeInputProps {
  cartValue?: number;
  userEmail?: string;
  onSuccess?: (result: any) => void;
  onError?: (error: string) => void;
}

interface AppliedPromoCode {
  code: string;
  name: string;
  description?: string;
  ruleMatched?: boolean;
  autoAddedProduct?: boolean;
}

export function PromoCodeInput({ 
  cartValue, 
  userEmail, 
  onSuccess, 
  onError 
}: PromoCodeInputProps) {
  const [code, setCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromoCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const { openCart } = useCart();
  const router = useRouter();

  // Check for existing applied promo code on mount
  useEffect(() => {
    checkAppliedPromoCode();
  }, []);

  async function checkAppliedPromoCode() {
    try {
      const response = await fetch('/api/promo-codes/apply');
      if (response.ok) {
        const data = await response.json();
        if (data.applied && data.code) {
          setAppliedPromo({
            code: data.code,
            name: data.code,
            description: data.message || 'Promo code applied'
          });
        }
      }
    } catch (error) {
      // Silently fail - no applied promo code
      console.warn('Failed to check applied promo code:', error);
    }
  }

  async function handleApplyPromoCode() {
    if (!code.trim()) {
      setError('Please enter a promo code');
      return;
    }

    setIsApplying(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/promo-codes/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code.trim()
        }),
      });

      const data = await response.json();

      if (data.success) {
        const appliedCodeData: AppliedPromoCode = {
          code: data.code,
          name: data.rule?.name || data.code,
          description: data.message,
          ruleMatched: data.ruleMatched,
          autoAddedProduct: data.autoAddedProduct
        };
        
        setAppliedPromo(appliedCodeData);
        setSuccess(data.message);
        setCode('');
        
        // If product was auto-added, refresh cart immediately and then open it
        if (data.autoAddedProduct) {
          // Force cart to refresh from server
          router.refresh();
          
          setTimeout(() => {
            openCart();
          }, 500); // Shorter delay since we're refreshing first
        }
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(data);
        }

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to apply promo code');
        if (onError) {
          onError(data.error || 'Failed to apply promo code');
        }
      }
    } catch (error) {
      const errorMessage = 'Failed to apply promo code. Please try again.';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsApplying(false);
    }
  }

  async function handleRemovePromoCode() {
    try {
      const response = await fetch('/api/promo-codes/apply', {
        method: 'DELETE',
      });

      if (response.ok) {
        setAppliedPromo(null);
        setSuccess('Promo code removed');
        
        // Clear success message after 2 seconds
        setTimeout(() => setSuccess(null), 2000);
      } else {
        setError('Failed to remove promo code');
      }
    } catch (error) {
      setError('Failed to remove promo code');
    }
  }

  function handleKeyPress(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyPromoCode();
    }
  }

  return (
    <div className="border-t border-neutral-200 pt-4 pb-2">
      {/* Header with inline applied promo code */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TagIcon className="h-4 w-4 text-neutral-600" />
          <h3 className="text-sm font-medium text-neutral-900">Promo Code</h3>
          {appliedPromo && (
            <div className="flex items-center gap-2 ml-2">
              <div className="inline-flex items-center rounded-sm px-[0.625rem] py-[0.125rem] text-sm font-normal transition-colors bg-black text-white">
                <CheckIcon className="h-3 w-3 mr-1.5" />
                {appliedPromo.code}
              </div>
            </div>
          )}
        </div>
        {appliedPromo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemovePromoCode}
            className="h-6 px-2 text-xs text-neutral-600 hover:text-red-600 hover:bg-red-50"
          >
            Remove
          </Button>
        )}
      </div>

      {/* Input section - only show when no promo applied */}
      {!appliedPromo && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              placeholder="Enter promo code"
              disabled={isApplying}
              className={clsx(
                'flex-1 px-3 py-2 text-sm border rounded-md transition-all duration-200',
                'focus:outline-none focus:ring-1 focus:ring-neutral-400 focus:border-neutral-400',
                'disabled:bg-neutral-50 disabled:cursor-not-allowed disabled:text-neutral-500',
                error 
                  ? 'border-red-300 bg-red-50 text-red-900 placeholder-red-400' 
                  : 'border-neutral-300 bg-white hover:border-neutral-400 placeholder-neutral-500'
              )}
            />
            <Button
              onClick={handleApplyPromoCode}
              disabled={isApplying || !code.trim()}
              size="sm"
              className={clsx(
                'px-4 text-sm transition-all duration-200',
                isApplying || !code.trim()
                  ? 'bg-neutral-100 text-neutral-400 border-neutral-200'
                  : 'bg-neutral-900 text-white hover:bg-neutral-700'
              )}
            >
              {isApplying ? (
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-3 animate-spin rounded-full border border-white border-t-transparent"></div>
                  <span>Applying</span>
                </div>
              ) : (
                'Apply'
              )}
            </Button>
          </div>

          {/* Compact error message */}
          {error && (
            <div className="flex items-center gap-2 p-2 rounded-md bg-red-50 border border-red-200">
              <XMarkIcon className="h-3 w-3 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
        </div>
      )}

      {/* Success message - appears briefly */}
      {success && (
        <div className="mt-2 flex items-center gap-2 p-2 rounded-md bg-green-50 border border-green-200">
          <CheckIcon className="h-3 w-3 text-green-600 flex-shrink-0" />
          <p className="text-xs text-green-700">{success}</p>
        </div>
      )}

      {/* Compact helper text */}
      {!appliedPromo && (
        <p className="text-xs text-neutral-500 mt-2">
          Valid codes will be applied at checkout.
        </p>
      )}
    </div>
  );
} 